// Turn a human name (a bug title or project name) into a safe filename base: keep only a conservative
// set of filename-safe characters (letters, digits, and a few separators), collapse runs of unsafe
// characters and whitespace into single hyphens, and trim. Falls back to `fallback` when nothing
// usable remains (e.g. a title that was only punctuation).
export function sanitizeFilename(name: string, fallback = 'bug-report'): string {
  const cleaned = name
    .normalize('NFKD')
    // Replace any character outside the safe allow-list (this also removes path separators and
    // control characters) with a space, so the next step folds them into single hyphens.
    .replace(/[^a-zA-Z0-9._-]+/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    // Avoid a leading dot producing a hidden/extension-only filename.
    .replace(/^[.]+/, '')
    // Keep filenames a reasonable length.
    .slice(0, 120)
    .replace(/-+$/, '')
  return cleaned || fallback
}

// Trigger a browser download of `content` as a file named `filename`. Creates a Blob, clicks a
// transient anchor, then revokes the object URL. Used to save bug reports as `.md` files.
export function downloadTextFile(filename: string, content: string, mimeType = 'text/markdown;charset=utf-8'): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.rel = 'noopener'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    // Revoke after the click has been dispatched so the download isn't cancelled mid-flight.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}
